import { TextView } from "@components";
import React, { useEffect, useState } from "react";
import ContentEditable from "react-contenteditable";
import { useNavigate } from "react-router-dom";
import { colors } from "../../utils";
import * as Styled from "./styles";
import { transformEditorText } from "../../utils/helpers";
import WheelComponent from "../WheelComponent";
import { useReducedMotion } from "framer-motion";
import { searchItemVariants } from "./searchMotion";
import { getWheelBackground } from "../WheelGallery/PrimaryWheel/primaryWheelHelpers";
import { CardWallpaperBackground } from "../WheelGallery/WheelListing/styles";

export default function WheelItem({
  item,
  setModalOpen = null,
  setOnLeave = null,
  wheelInformation = null,
  prevIndexRef = null,
  setShowResults = null,
  handleNavigate = null,
  kbdNav = false,
  isHighlighted = false,
  optionId = undefined,
}) {
  const [chartState, setChartState] = useState([]);
  const {
    wheel_image,
    wheel_name,
    user_profile,
    id,
    wheel_segments,
    version,
    wallpaper,
    wallpaper_settings,
  } = item;
  const { first_name, last_name } = user_profile;

  // GS-7.7 (qa-validator round 3, F-R3-A/C follow-up): match WheelCard.jsx's
  // OWN call shape exactly, byte-for-byte, instead of routing through
  // resolvePrimaryBackground/getBackground first. Two reasons:
  //   1. (F-R3-C) resolvePrimaryBackground pre-wraps an image URL into
  //      `url(...) center/cover no-repeat` before handing it to
  //      CardWallpaperBackground -- which ALSO does its own isImageUrl-wrap
  //      internally. That double-processing rendered identical CSS today only
  //      by accident (the pre-wrapped string fails CardWallpaperBackground's
  //      own `isImageUrl` regex and falls through to its raw-string branch).
  //      Passing the RAW resolved value, like WheelCard does, is the single
  //      correct path and removes the coincidence.
  //   2. (F-R3-A) WheelCard also derives wallpaperHueColor/wallpaperHuePercentage
  //      from the SAME wallpaper_settings object and forwards them to
  //      CardWallpaperBackground. This row skipped that, so a hue-tinted wheel
  //      rendered with NO tint here while every other card surface (Gallery,
  //      Explore, ExploreHero) applies it -- measurably not "the SAME
  //      background the Gallery card would" as originally claimed. Forwarding
  //      the same two props closes that gap.
  //
  // resolvePrimaryBackground always resolves to SOMETHING, even "#F6F7F9" for
  // a wheel with zero wallpaper data -- rendering the layer unconditionally
  // would paint a new grey box behind every search row lacking real wallpaper
  // data (likely the common case per the sampled live payload), an unmeasured
  // visual change with no live surface to check yet. Gate on the wheel
  // ACTUALLY carrying data, using the SAME resolved value the layer paints
  // (so the gate can never disagree with what it gates, per round 2's R2-F6).
  const resolvedWallpaperBg =
    version === 1 ? wallpaper : getWheelBackground(wallpaper_settings);
  const hasWallpaperData = Boolean(resolvedWallpaperBg);

  const wallpaperSettingsForHue = wallpaper_settings ?? {};
  const wallpaperHueColor =
    wallpaperSettingsForHue.wallpaperHueColor ??
    wallpaperSettingsForHue.wallpaper_hue_color;
  const wallpaperHuePercentage =
    wallpaperSettingsForHue.wallpaperHuePercentage ??
    wallpaperSettingsForHue.wallpaper_hue_percentage ??
    0;

  const nav = useNavigate();

  useEffect(() => {
    const wheelUpdatedData = wheel_segments?.map((obj, index) => ({
      label: "blank",
      value: "1",
      isBlank: false,
      ...obj,
    }));

    const wheelUpdatedDataSorted = wheelUpdatedData?.sort(
      (a, b) => a.position - b.position
    );

    setChartState(wheelUpdatedDataSorted);
  }, [item]);

  const onClick = () => {
    setShowResults(false);

    // Call handleNavigate if it exists
    if (handleNavigate) {
      handleNavigate();
      return;
    }

    if (setOnLeave && setModalOpen) {
      const currentIndex = wheelInformation?.currentIndex;

      if (prevIndexRef.current !== currentIndex) {
        setOnLeave(() => () => {
          if (version == 2) {
            nav(`/wheel/${id}`);
          } else {
            nav(`/wheel/${id}`);
          }
        });
        setModalOpen(true);
        prevIndexRef.current = currentIndex;
      } else {
        if (version == 2) {
          nav(`/wheel/${id}`);
        } else {
          nav(`/wheel/${id}`);
        }
      }
    } else {
      if (version == 2) {
        nav(`/wheel/${id}`);
      } else {
        nav(`/wheel/${id}`);
      }
    }
  };

  // Honor OS reduced-motion: no slide-in, hover scale, or tap bounce.
  const reduceMotion = useReducedMotion();
  const itemVariants = searchItemVariants(reduceMotion);

  // EW-1182: additive keyboard-nav wiring (see UserItem). Gated on kbdNav so a
  // flag-OFF render is byte-identical.
  const kbdProps = kbdNav
    ? {
        "data-search-result-item": "",
        role: "option",
        id: optionId,
        "aria-selected": isHighlighted,
        style: isHighlighted
          ? {
              backgroundColor: colors.Highlight,
              outline: `2px solid ${colors.PrimaryPurple500}`,
              outlineOffset: "-2px",
            }
          : undefined,
      }
    : {};

  return (
    <Styled.SearchItem
      onClick={onClick}
      variants={itemVariants}
      initial="initial"
      animate="animate"
      whileHover="hover"
      whileTap="tap"
      {...kbdProps}
    >
      <Styled.SearchItemMedia>
        {/* GS-7.7: the search-wheels payload already carries wallpaper /
            wallpaper_settings (MEASURED live, fleet/w3-search-2 R2 2026-07-26),
            but this row only ever rendered `url: wheel_image` -> the 60px
            thumbnail could not reflect the wheel's real wallpaper/pattern look,
            a parity gap vs every other card surface. Reuse the SAME shared
            background layer + the SAME wallpaper_settings-derivation shape
            WheelCard.jsx uses (raw resolved value + hue props), not a
            re-derivation. SearchItemMedia is already `position:relative;
            overflow:hidden; border-radius:4px` (matching this layer's own
            radius, qa-validator round-3 F-R3-D), so the absolute background
            layer clips to the 60px box with no corner mismatch. Gated on
            hasWallpaperData so a wheel with no wallpaper data at all renders
            byte-identical to before this fix, not a new grey fallback box. */}
        {hasWallpaperData && (
          <CardWallpaperBackground
            bg={resolvedWallpaperBg}
            wallpaperHueColor={wallpaperHueColor}
            wallpaperHuePercentage={wallpaperHuePercentage}
          />
        )}
        <div style={{ position: "relative", zIndex: 1 }}>
          <WheelComponent
            width={`${Styled.SEARCH_RESULT_WHEEL_SIZE_PX}px`}
            height={`${Styled.SEARCH_RESULT_WHEEL_SIZE_PX}px`}
            config={{
              isSmallView: true,
              showTooltips: false,
              // search-comp-vY-02: a no-cover wheel's brand-logo placeholder
              // otherwise renders ~26px (BRAND_LOGO_INSET_PCT's 17% breathing-room
              // inset) inside this 60px ring while a real-cover wheel's portrait
              // fills it at ~39px -- same ring, inconsistent inner image size.
              // Zero the inset here so both cases fill the ring identically; every
              // other isBrandLogo surface (Gallery, Explore, View Wheel) keeps the
              // default inset unchanged.
              brandLogoInsetPct: 0,
            }}
            userData={{
              // Stable per-wheel identity. Was Math.random(), which changed on
              // every render, so WheelComponent's memo comparator (keyed on
              // userData.id) never matched -> the mini-wheel portrait recomputed
              // and flickered on every keystroke-debounce while typing.
              id,
              url: wheel_image,
            }}
            maxSelectedArcs={0}
            arcThickness={-2}
            chartData={chartState}
            margin={1}
            containerDivStyle={{
              width: `${Styled.SEARCH_RESULT_WHEEL_SIZE_PX}px`,
              height: `${Styled.SEARCH_RESULT_WHEEL_SIZE_PX}px`,
              overflow: "hidden",
            }}
          />
        </div>
      </Styled.SearchItemMedia>

      <Styled.SearchItemDetails>
        <TextView variant="subtitle" style={{ fontSize: 14 }}>
          <ContentEditable html={transformEditorText(wheel_name)} disabled />
        </TextView>
        <TextView color={colors.lightGrey}>
          By {first_name} {last_name}
        </TextView>
      </Styled.SearchItemDetails>
    </Styled.SearchItem>
  );
}
